<script lang="ts">
import ElClipToAnchor, { elClipToAnchorPositions } from '@/ElClipToAnchor.vue';
import { useMobile } from '@/composable/mobileComposable';
export const elTooltipPositions = elClipToAnchorPositions;
export type ElTooltipPosition = (typeof elTooltipPositions)[number];
</script>
<script lang="ts" setup>
import { computed, ref, watch } from 'vue';
import CustomTransition from '@/_CustomTransition.vue';

const props = withDefaults(
  defineProps<{
    title: string;
    content?: string;
    position?: ElTooltipPosition;
    /** Time in ms before the tooltip appears */
    delayTime?: string | number;
    /** Hide tooltip when tooltip's title changes */
    hideOnChange?: boolean;
    hideOnMobile?: boolean;
    allowNewLines?: boolean;
  }>(),
  {
    content: undefined,
    position: 'top',
    delayTime: 200,
    hideOnChange: false,
    hideOnMobile: false,
    allowNewLines: false,
  },
);

const anchor = ref<HTMLElement>();
const show = ref(false);
let showDebounceTimeout: ReturnType<typeof setTimeout> | undefined;

const { isMobile } = useMobile();

const sanitizedDelayTime = computed(() =>
  typeof props.delayTime === 'string' && isNaN(Number(props.delayTime)) ? 200 : Number(props.delayTime),
);

const onMouseEnter = () => {
  if (!props.title.length || (props.hideOnMobile && isMobile.value)) return;

  showDebounceTimeout = setTimeout(() => {
    if (showDebounceTimeout) clearTimeout(showDebounceTimeout);
    show.value = true;
  }, sanitizedDelayTime.value);
};

const onMouseLeave = () => {
  show.value = false;
  if (showDebounceTimeout) clearTimeout(showDebounceTimeout);
};

watch(
  () => props.title,
  v => {
    if (!v.length) show.value = false;
  },
);
watch([() => props.hideOnChange, () => props.title], v => {
  const hideOnChange = v[0];
  if (hideOnChange) {
    show.value = false;
  }
});
</script>

<template>
  <div>
    <div ref="anchor" @mouseenter="onMouseEnter" @mouseleave="onMouseLeave">
      <slot></slot>
    </div>
    <CustomTransition name="slide-y-fade">
      <ElClipToAnchor
        v-if="anchor && show"
        :anchor="anchor"
        :position="position"
        :distance-from-anchor="14"
        class="max-w-96 rounded-2xl bg-neutral-100 p-3 text-xs drop-shadow text-neutral-darker"
      >
        <p class="font-semibold" :class="{ 'whitespace-pre-line': allowNewLines }">
          {{ title }}
        </p>
        <p v-if="content?.length" class="mt-1" :class="{ 'whitespace-pre-line': allowNewLines }">{{ content }}</p>
      </ElClipToAnchor>
    </CustomTransition>
  </div>
</template>
